import React from 'react';
import {
  Box, Typography, Popover, IconButton, Button, Tooltip, Divider,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import BookmarkRemoveIcon from '@mui/icons-material/BookmarkRemove';

interface NewsMenuProps {
  seenCount: number;
  seenInViewCount: number;
  bookmarkCount: number;
  loadedPages: number;
  onClearSeenInView: () => void;
  onClearSeen: () => void;
  onClearBookmarks: () => void;
  onResetFeed: () => void;
}

const ROW = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1,
  px: 2,
  py: 1,
} as const;

const LABEL = { fontSize: { xs: '0.6rem', sm: '0.65rem' } } as const;
const HINT = {
  fontSize: { xs: '0.5rem', sm: '0.55rem' },
  color: 'rgba(255,255,255,0.4)',
  mt: 0.25,
} as const;

/**
 * ニュースの片付け用メニュー。
 *
 * 媒体設定（表示する媒体を選ぶ）とは目的が違うので、隣の別アイコンに分けている。
 * 片方は「何を見るか」、こちらは「溜まったものを捨てる」。
 */
const NewsMenu: React.FC<NewsMenuProps> = ({
  seenCount,
  seenInViewCount,
  bookmarkCount,
  loadedPages,
  onClearSeenInView,
  onClearSeen,
  onClearBookmarks,
  onResetFeed,
}) => {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const close = () => setAnchor(null);

  const run = (fn: () => void, confirmMessage?: string) => () => {
    if (confirmMessage && !window.confirm(confirmMessage)) return;
    fn();
    close();
  };

  return (
    <>
      <Tooltip title="表示の管理">
        <IconButton
          size="small"
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            color: 'rgba(255,255,255,0.55)',
            '&:hover': { color: 'rgba(255,255,255,0.85)' },
          }}
        >
          <SettingsIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              width: 288,
              borderRadius: 3,
              backgroundColor: 'rgba(0,0,0,0.78)',
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.14)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
              backgroundImage: 'none',
            },
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.75, pb: 1 }}>
          <Typography sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' }, fontWeight: 600 }}>
            表示の管理
          </Typography>
          <Typography sx={HINT}>
            いずれもこの端末のデータだけを消します
          </Typography>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

        {/* 「既読を消す」だと記事そのものが消えると読まれる。
            結果の状態（未読に戻る）を言う。記事を消す操作との差も
            ボタンの語（戻す / 消す）で見分けられるようにしている。

            既読はタブに属さない（同じ記事が複数タブに出る）ので、
            「タブ別」ではなく「今読み込んでいる分」で区切る */}
        <Box sx={ROW}>
          <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.45)', mt: 0.25 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={LABEL}>この一覧を未読に戻す</Typography>
            <Typography sx={HINT}>
              いま開いているタブに読み込まれている既読 {seenInViewCount} 件が対象。
            </Typography>
          </Box>
          <Button
            size="small"
            disabled={seenInViewCount === 0}
            onClick={run(onClearSeenInView)}
            sx={{ fontSize: { xs: '0.5rem', sm: '0.55rem' }, minWidth: 0 }}
          >
            戻す
          </Button>
        </Box>

        <Box sx={ROW}>
          <RadioButtonUncheckedIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.45)', mt: 0.25 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={LABEL}>すべて未読に戻す</Typography>
            <Typography sx={HINT}>
              記録している既読 {seenCount} 件すべて。
            </Typography>
          </Box>
          <Button
            size="small"
            disabled={seenCount === 0}
            onClick={run(onClearSeen, '既読の記録をすべて消して未読に戻します。記事自体は消えません。よろしいですか？')}
            sx={{ fontSize: { xs: '0.5rem', sm: '0.55rem' }, minWidth: 0 }}
          >
            戻す
          </Button>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

        <Box sx={ROW}>
          <BookmarkRemoveIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.45)', mt: 0.25 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={LABEL}>保存した記事をすべて消す</Typography>
            <Typography sx={HINT}>
              {bookmarkCount} 件。元に戻せません
            </Typography>
          </Box>
          <Button
            size="small"
            color="error"
            disabled={bookmarkCount === 0}
            onClick={run(onClearBookmarks, `保存した記事 ${bookmarkCount} 件をすべて消します。元に戻せません。よろしいですか？`)}
            sx={{ fontSize: { xs: '0.5rem', sm: '0.55rem' }, minWidth: 0 }}
          >
            消す
          </Button>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

        {/* スクロールし続けると読み込み済みのカードが積み上がる。
            ページを再読み込みしなくても捨てられるようにしておく */}
        <Box sx={ROW}>
          <RestartAltIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.45)', mt: 0.25 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={LABEL}>キャッシュをリセット</Typography>
            <Typography sx={HINT}>
              読み込み済みの {loadedPages} ページ分を破棄して先頭から読み直します。
              スクロールし続けて重くなったときに
            </Typography>
          </Box>
          <Button
            size="small"
            disabled={loadedPages <= 1}
            onClick={run(onResetFeed)}
            sx={{ fontSize: { xs: '0.5rem', sm: '0.55rem' }, minWidth: 0 }}
          >
            実行
          </Button>
        </Box>
      </Popover>
    </>
  );
};

export default NewsMenu;
